import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  ArrowRightLeft,
  Bot,
  CheckCircle2,
  Clock,
  Crosshair,
  FlaskConical,
  Radio,
  Shield,
  TrendingUp,
  Wallet,
  XCircle,
  Zap,
} from "lucide-react";
import Header from "../components/common/header";
import Footer from "../components/common/footer";
import WalletReadiness from "../components/WalletReadiness";

const STATE_URL = "/auto-trader-state.json";
const WS_URL = "wss://cryptospace.network/wallet-feed";

const BOT_DEFINITIONS = [
  {
    id: "new-token-sniper",
    name: "New Token Sniper",
    icon: <Crosshair />,
    intent: "Catch fresh liquidity and early token activity before the market fully discovers it.",
    signals: ["NEW_CONTRACT_DEPLOY", "FIRST_TOKEN_ACTIVITY", "MINT", "REAL_SWAP", "SWAP_TOKENS"],
    rules: ["liquidity >= $20K", "token risk not blocked", "age window < 10m", "small test size"],
  },
  {
    id: "momentum",
    name: "Momentum Bot",
    icon: <TrendingUp />,
    intent: "Follow buy pressure after repeated real swaps and volume acceleration.",
    signals: ["REAL_SWAP", "SWAP_TOKENS", "LARGE_VALUE_TX"],
    rules: ["3+ related events", "liquidity >= $50K", "fresh signal < 60s", "exit on fade"],
  },
  {
    id: "arbitrage-scanner",
    name: "Arbitrage Scanner",
    icon: <ArrowRightLeft />,
    intent: "Look for price differences between routes before any execution is considered.",
    signals: ["REAL_SWAP", "SWAP_AMOUNT", "SWAP_DIRECTION"],
    rules: ["quote both routes", "gas/slippage included", "profit threshold", "paper only"],
  },
  {
    id: "risk-guard",
    name: "Risk Guard",
    icon: <Shield />,
    intent: "Block or downsize candidates before a test wallet is allowed to validate them.",
    signals: ["BOT_LIKE_TX", "GAS_SPIKE", "COMPLEX_TX", "CONTRACT_CALL", "ZERO_VALUE_LOGIC_CALL"],
    rules: ["honeypot check", "sell simulation", "tax/liquidity gates", "network stress"],
  },
];

const SIGNAL_LABELS = {
  NEW_CONTRACT_DEPLOY: "new contract",
  FIRST_TOKEN_ACTIVITY: "first activity",
  REAL_SWAP: "real swap",
  SWAP_TOKENS: "swap tokens",
  SWAP_AMOUNT: "swap amount",
  SWAP_DIRECTION: "swap direction",
  LARGE_VALUE_TX: "large value",
  BOT_LIKE_TX: "bot-like",
  GAS_SPIKE: "gas spike",
  COMPLEX_TX: "complex tx",
  CONTRACT_CALL: "contract call",
  ZERO_VALUE_LOGIC_CALL: "logic call",
  MINT: "mint",
};

function age(value) {
  if (!value) return "waiting";
  const diff = Date.now() - new Date(value).getTime();
  if (Number.isNaN(diff)) return "waiting";
  if (diff < 0) return "now";
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function money(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function short(value) {
  if (!value) return "unknown";
  return value.length > 18 ? `${value.slice(0, 8)}...${value.slice(-6)}` : value;
}

function normalizeLiveSignal(message) {
  const payload = message?.payload || message || {};
  const event = message?.event || payload.event || "UNKNOWN";
  const net = (payload.net || payload.network || message?.net || "unknown").toLowerCase();
  const tokenAddress = payload.buy || payload.tokenIn || payload.tokenOut || payload.token || payload.contract || payload.to || "unknown";
  const tokenSymbol = payload.buySymbol || payload.tokenInSymbol || payload.tokenOutSymbol || payload.symbol || payload.tokenSymbol || short(tokenAddress);
  return {
    event,
    net: net === "bsc" ? "bnb" : net,
    txHash: payload.txHash || payload.tx || message?.txHash,
    tokenAddress: String(tokenAddress).toLowerCase(),
    tokenSymbol,
    amount: Number(payload.amountUsd || payload.amountInUsd || payload.valueUsd || payload.amountIn || payload.amount || payload.value || 0) || 0,
    receivedAt: new Date().toISOString(),
    raw: message,
  };
}

function scoreCandidate(bot, signal, state) {
  const positions = state?.positions || [];
  const rejected = state?.rejectedSignals || [];
  const sameTokenOpen = positions.some((position) => position.tokenAddress === signal.tokenAddress && position.net === signal.net);
  const recentRejects = rejected.filter((item) => item.tokenAddress === signal.tokenAddress && item.net === signal.net).length;
  const amountBonus = Math.min(22, Math.log10(Math.max(signal.amount, 0) + 1) * 4);
  let score = amountBonus;
  const reasons = [];

  if (bot.id === "new-token-sniper") {
    if (signal.event === "NEW_CONTRACT_DEPLOY") score += 28;
    if (signal.event === "FIRST_TOKEN_ACTIVITY") score += 34;
    if (signal.event === "MINT") score += 18;
    if (signal.event === "REAL_SWAP" || signal.event === "SWAP_TOKENS") score += 22;
    reasons.push("fresh-token lifecycle signal");
  }

  if (bot.id === "momentum") {
    const recentSameToken = state?.liveSignals?.filter((item) => item.tokenAddress === signal.tokenAddress && item.net === signal.net).length || 0;
    if (signal.event === "REAL_SWAP") score += 34;
    if (signal.event === "SWAP_TOKENS") score += 30;
    if (signal.event === "LARGE_VALUE_TX") score += 24;
    if (recentSameToken >= 3) {
      score += 18;
      reasons.push(`${recentSameToken} related events in live window`);
    }
    reasons.push("buy pressure candidate");
  }

  if (bot.id === "arbitrage-scanner") {
    if (signal.event === "REAL_SWAP") score += 22;
    if (signal.event === "SWAP_AMOUNT") score += 26;
    if (signal.event === "SWAP_DIRECTION") score += 24;
    reasons.push("requires route quote comparison");
  }

  if (bot.id === "risk-guard") {
    if (signal.event === "BOT_LIKE_TX") score += 40;
    if (signal.event === "GAS_SPIKE") score += 34;
    if (signal.event === "COMPLEX_TX") score += 24;
    if (signal.event === "CONTRACT_CALL" || signal.event === "ZERO_VALUE_LOGIC_CALL") score += 16;
    reasons.push("risk-only signal");
  }

  if (amountBonus > 0) reasons.push(`amount bonus ${amountBonus.toFixed(1)}`);
  if (sameTokenOpen) {
    score -= 30;
    reasons.push("already open in paper state");
  }
  if (recentRejects > 0) {
    score -= Math.min(20, recentRejects * 4);
    reasons.push(`${recentRejects} recent risk rejects`);
  }

  const decision = bot.id === "risk-guard"
    ? "WATCH"
    : score >= 60
      ? "PAPER_CANDIDATE"
      : score >= 38
        ? "WATCH"
        : "IGNORE";

  return {
    id: `${bot.id}-${signal.txHash || signal.tokenAddress}-${signal.receivedAt}`,
    botId: bot.id,
    botName: bot.name,
    event: signal.event,
    net: signal.net,
    tokenSymbol: signal.tokenSymbol,
    tokenAddress: signal.tokenAddress,
    txHash: signal.txHash,
    score: Math.round(score),
    decision,
    suggestedSizeUsd: decision === "PAPER_CANDIDATE" ? 100 : 0,
    expiresInMs: bot.id === "arbitrage-scanner" ? 8000 : 30000,
    reasons,
    receivedAt: signal.receivedAt,
  };
}

function buildStateCandidates(state) {
  const positions = (state?.positions || []).slice(0, 8).map((position) => ({
    event: position.entryEvent || "REAL_SWAP",
    net: position.net,
    tokenAddress: position.tokenAddress,
    tokenSymbol: position.tokenSymbol,
    txHash: position.entryTxHash,
    amount: position.sizeUsd || 0,
    receivedAt: position.openedAt || state?.updatedAt || new Date().toISOString(),
  }));

  return positions.flatMap((signal) =>
    BOT_DEFINITIONS
      .filter((bot) => bot.signals.includes(signal.event))
      .map((bot) => scoreCandidate(bot, signal, { ...state, liveSignals: [] })),
  );
}

export default function BotLabPage() {
  const [state, setState] = useState(null);
  const [liveSignals, setLiveSignals] = useState([]);
  const [wsStatus, setWsStatus] = useState("connecting");
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadState() {
      try {
        const response = await fetch(`${STATE_URL}?t=${Date.now()}`, { cache: "no-store" });
        if (!response.ok) throw new Error("state unavailable");
        const next = await response.json();
        if (!cancelled) {
          setState(next);
          setLoadError("");
        }
      } catch {
        if (!cancelled) setLoadError("Auto-trader state is not available yet.");
      }
    }

    loadState();
    const timer = window.setInterval(loadState, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    const ws = new WebSocket(WS_URL);
    ws.onopen = () => setWsStatus("connected");
    ws.onerror = () => setWsStatus("waiting");
    ws.onclose = () => setWsStatus("closed");
    ws.onmessage = (event) => {
      try {
        const next = normalizeLiveSignal(JSON.parse(event.data));
        setLiveSignals((prev) => [next, ...prev].slice(0, 80));
      } catch {
        // Keep the lab resilient while the feed is noisy.
      }
    };
    return () => ws.close();
  }, []);

  const enrichedState = useMemo(() => ({ ...state, liveSignals }), [state, liveSignals]);

  const liveCandidates = useMemo(() => {
    return liveSignals
      .slice(0, 30)
      .flatMap((signal) =>
        BOT_DEFINITIONS
          .filter((bot) => bot.signals.includes(signal.event))
          .map((bot) => scoreCandidate(bot, signal, enrichedState)),
      )
      .sort((a, b) => b.score - a.score)
      .slice(0, 16);
  }, [enrichedState, liveSignals]);

  const stateCandidates = useMemo(() => buildStateCandidates(state), [state]);
  const candidates = liveCandidates.length ? liveCandidates : stateCandidates;
  const paperReady = candidates.filter((candidate) => candidate.decision === "PAPER_CANDIDATE").length;
  const watchOnly = candidates.filter((candidate) => candidate.decision === "WATCH").length;
  const lastSignalAt = liveSignals[0]?.receivedAt || state?.health?.lastSignalAt;

  return (
    <div className="bot-lab-page">
      <Header />
      <main className="bot-lab-shell">
        <section className="bot-lab-hero">
          <div>
            <div className="bot-lab-kicker">
              <FlaskConical size={16} />
              Multi-bot paper lab
            </div>
            <h1>Test several trading bots against the same live signal stream.</h1>
            <p>
              Sniper, momentum, arbitrage, and risk guard strategies run as
              paper candidates. The connected wallet is only for manual
              validation after a candidate passes risk review.
            </p>
          </div>
          <div className="bot-lab-live-card">
            <span className={wsStatus === "connected" ? "live-dot" : "live-dot waiting"} />
            <small>Live feed</small>
            <strong>{wsStatus}</strong>
            <span>last signal {age(lastSignalAt)}</span>
          </div>
        </section>

        <WalletReadiness />

        {loadError && <div className="bot-lab-warning">{loadError}</div>}

        <section className="bot-lab-stats">
          <Metric icon={<Activity />} label="Live window" value={liveSignals.length} />
          <Metric icon={<Bot />} label="Strategies" value={BOT_DEFINITIONS.length} />
          <Metric icon={<Zap />} label="Paper candidates" value={paperReady} />
          <Metric icon={<Clock />} label="Watch only" value={watchOnly} />
          <Metric icon={<Wallet />} label="Execution" value="Manual" />
          <Metric icon={<Shield />} label="Mode" value="Paper" />
        </section>

        <section className="bot-lab-bot-grid">
          {BOT_DEFINITIONS.map((bot) => (
            <article className="bot-lab-bot-card" key={bot.id}>
              <div className="bot-lab-bot-head">
                {bot.icon}
                <div>
                  <h2>{bot.name}</h2>
                  <p>{bot.intent}</p>
                </div>
              </div>
              <div className="bot-lab-chip-row">
                {bot.signals.map((signal) => (
                  <span key={signal}>{SIGNAL_LABELS[signal] || signal}</span>
                ))}
              </div>
              <ul>
                {bot.rules.map((rule) => <li key={rule}>{rule}</li>)}
              </ul>
            </article>
          ))}
        </section>

        <section className="bot-lab-grid">
          <Panel title="Strategy Candidates" icon={<CheckCircle2 />}>
            {candidates.length ? (
              <div className="bot-lab-candidate-list">
                {candidates.map((candidate) => (
                  <CandidateRow candidate={candidate} key={candidate.id} />
                ))}
              </div>
            ) : (
              <Empty text="Waiting for live signals that match a strategy." />
            )}
          </Panel>

          <Panel title="Risk And Execution Gate" icon={<Shield />}>
            <div className="bot-lab-flow">
              <FlowStep title="Candidate" text="A strategy proposes a paper opportunity with reasons and expiry." />
              <FlowStep title="Risk Guard" text="Token Risk, liquidity, tax, and network stress decide allow/watch/block." />
              <FlowStep title="Paper Order" text="The lab records a simulated order, size, route, and expected slippage." />
              <FlowStep title="Manual Test Wallet" text="A connected test wallet can validate route readiness without auto-submit." />
              <FlowStep title="Live Locked" text="No private key, no automatic transaction, no real execution from this page." />
            </div>
          </Panel>
        </section>

        <section className="bot-lab-grid">
          <Panel title="Current Paper State" icon={<Radio />}>
            <div className="bot-lab-state-grid">
              <StateCell label="Processed" value={state?.stats?.signals || 0} />
              <StateCell label="Entries" value={state?.stats?.entries || 0} />
              <StateCell label="Exits" value={state?.stats?.exits || 0} />
              <StateCell label="Open" value={(state?.positions || []).length} />
              <StateCell label="Rejected" value={state?.stats?.rejected || 0} />
              <StateCell label="PnL" value={money(state?.stats?.realizedPnlUsd)} />
            </div>
            <div className="bot-lab-state-note">
              Public state updates independently from live feed. A fresh file
              does not always mean the strategy engine processed a fresh signal.
            </div>
          </Panel>

          <Panel title="Recent Live Signals" icon={<AlertTriangle />}>
            {liveSignals.length ? (
              <div className="bot-lab-signal-list">
                {liveSignals.slice(0, 10).map((signal, index) => (
                  <div className="bot-lab-signal-row" key={`${signal.txHash || signal.event}-${index}`}>
                    <strong>{SIGNAL_LABELS[signal.event] || signal.event}</strong>
                    <span>{signal.net} | {signal.tokenSymbol || short(signal.tokenAddress)} | {age(signal.receivedAt)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <Empty text="Live feed is connecting or quiet." />
            )}
          </Panel>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function Metric({ icon, label, value }) {
  return (
    <article className="bot-lab-metric">
      {icon}
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}

function Panel({ title, icon, children }) {
  return (
    <section className="bot-lab-panel">
      <div className="bot-lab-panel-head">
        {icon}
        <h2>{title}</h2>
      </div>
      {children}
    </section>
  );
}

function CandidateRow({ candidate }) {
  const className = candidate.decision.toLowerCase().replace("_", "-");
  return (
    <article className={`bot-lab-candidate ${className}`}>
      <div>
        <span>{candidate.botName}</span>
        <strong>{candidate.tokenSymbol || short(candidate.tokenAddress)}</strong>
        <em>{candidate.net} | {SIGNAL_LABELS[candidate.event] || candidate.event} | {age(candidate.receivedAt)}</em>
      </div>
      <div>
        <b>{candidate.score}</b>
        <small>{candidate.decision.replace("_", " ")}</small>
      </div>
      <p>{candidate.reasons.join(", ")}</p>
      <footer>
        <span>size {money(candidate.suggestedSizeUsd)}</span>
        <span>expires {Math.round(candidate.expiresInMs / 1000)}s</span>
        {candidate.txHash && <span>{short(candidate.txHash)}</span>}
      </footer>
    </article>
  );
}

function FlowStep({ title, text }) {
  return (
    <div className="bot-lab-flow-step">
      <span><XCircle size={14} /></span>
      <div>
        <strong>{title}</strong>
        <p>{text}</p>
      </div>
    </div>
  );
}

function StateCell({ label, value }) {
  return (
    <div className="bot-lab-state-cell">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Empty({ text }) {
  return <div className="bot-lab-empty">{text}</div>;
}
